import { requests } from '../../helper/agent';
import { SubmissionError } from 'redux-form';
import {
  PATIENT_LIST_CLEAR_FILTERS,
  PATIENT_LIST_ERROR,
  PATIENT_LIST_RECEIVED,
  PATIENT_LIST_REQUEST,
  PATIENT_LIST_SET_FILTERS,
  PATIENT_LIST_SET_PAGE,
  PATIENT_LIST_UNLOAD,
  PATIENT_CONSULTANT_UPDATED,
  PATIENT_SINGLE_ERROR,
  PATIENT_SINGLE_RECEIVED,
  PATIENT_SINGLE_REQUEST,
  PATIENT_SINGLE_UNLOAD,
} from '../constants';

import * as API_ROUTES from '../../routing/api';
import { userLogout } from './user';
import { parseApiErrors } from '../../helper/apiUtils';

/**
 * MED-921 - reads only on the patient itself. Patient identity is never edited here: the API
 * exposes GetCollection and Get on `Patient` and nothing else.
 *
 * MED-1002 adds the one write on this screen, and it is not on the patient - it is a PATCH of
 * `noShortfall` on the patient/consultant pair. A pair is not created or deleted through the API
 * either; it exists because an invoice exists.
 */

export const patientListRequest = () => ({
  type: PATIENT_LIST_REQUEST,
});

export const patientListReceived = (data) => ({
  type: PATIENT_LIST_RECEIVED,
  data,
});

export const patientListError = (error) => ({
  type: PATIENT_LIST_ERROR,
  error,
});

export const patientListSetPage = (page) => ({
  type: PATIENT_LIST_SET_PAGE,
  page,
});

export const patientListSetFilters = (filters, routeKey) => ({
  type: PATIENT_LIST_SET_FILTERS,
  filters,
  routeKey,
});

export const patientListClearFilters = (routeKey) => ({
  type: PATIENT_LIST_CLEAR_FILTERS,
  routeKey,
});

export const patientListUnload = () => ({
  type: PATIENT_LIST_UNLOAD,
});

/**
 * Always paginated. The register holds 57,624 people, so `pagination: false` - which several
 * other lists in this app pass because their tables are short - would pull the lot into the
 * browser.
 */
export const patientListFetch = (page = 1, filters = {}) => {
  return (dispatch) => {
    dispatch(patientListRequest());

    return requests
      .get(API_ROUTES.PATIENTS, { ...filters, pagination: true, page })
      .then((response) => dispatch(patientListReceived(response)))
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          return dispatch(userLogout());
        } else if (error.response && error.response.status === 403) {
          return new SubmissionError(parseApiErrors({ _error: 'You are not allowed to do this.' }));
        }
        dispatch(patientListError(error));
      });
  };
};

export const patientConsultantUpdated = (data) => ({
  type: PATIENT_CONSULTANT_UPDATED,
  data,
});

/**
 * MED-1002 - mark, or unmark, this patient as "no shortfall" for one consultant.
 *
 * Per pair, never per patient. 2,242 people in the register are seen by more than one consultant,
 * and a decision not to chase somebody for a shortfall belongs to one consultant's dealings with
 * them - applying it to the whole person would silence bills they have never been asked about.
 *
 * Clearing the flag stops future suppressions and leaves the Suppressed Shortfalls screen exactly
 * as it was. That is deliberate: the record is of an event, not of the current setting, so
 * unmarking somebody cannot erase the evidence they were never told.
 */
export const patientConsultantSetNoShortfall = (patientConsultantId, noShortfall) => {
  return (dispatch) => {
    return requests
      .patch(`${API_ROUTES.PATIENT_CONSULTANTS}/${patientConsultantId}`, { noShortfall: Boolean(noShortfall) })
      .then((response) => dispatch(patientConsultantUpdated(response)))
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          return dispatch(userLogout());
        }
        throw new SubmissionError(parseApiErrors(error));
      });
  };
};

export const patientSingleRequest = () => ({
  type: PATIENT_SINGLE_REQUEST,
});

export const patientSingleReceived = (data) => ({
  type: PATIENT_SINGLE_RECEIVED,
  data,
});

export const patientSingleError = (error) => ({
  type: PATIENT_SINGLE_ERROR,
  error,
});

export const patientSingleUnload = () => ({
  type: PATIENT_SINGLE_UNLOAD,
});

/**
 * A refused patient (403) or a missing one (404) both end as an error state rather than an empty
 * screen. Consultant-side users are refused every patient endpoint, and the item route answers
 * 404 rather than 403 because the query extension gets there before the security expression - so
 * both have to be handled, and neither should look like "this patient has no invoices".
 */
export const patientSingleFetch = (id) => {
  return (dispatch) => {
    dispatch(patientSingleRequest());

    return requests
      .get(`${API_ROUTES.PATIENTS}/${id}`)
      .then((response) => dispatch(patientSingleReceived(response)))
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          return dispatch(userLogout());
        }
        dispatch(patientSingleError(error));
      });
  };
};
